import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";

import { EntityRefPicker } from "@/components/fields/entity-ref-picker";
import { ScreeningVenueRef } from "@/pages/screenings/schemas/screening.edit.form";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";

/**
 * The screening's cinema. Like the movie, the form holds the denormalized `venue` copy for
 * display and sends only `venueId`; the backend copies the venue document.
 *
 * The picked venue's `timezone` is kept on the form value because the start time is shown and
 * saved in the venue's zone — `ScreeningsEdit` reads it at submit.
 */
export function ScreeningVenueField({ id }: AutoFormFieldProps) {
  const { search } = useVenueRepository();
  const form = useFormContext();
  const venue: ScreeningVenueRef | null | undefined = useWatch({ name: id });

  const pick = (doc: any) => {
    if (!doc?.id || doc.id === venue?.id) return;
    form.setValue(
      id,
      {
        id: doc.id,
        name: doc.name ?? null,
        address: doc.address ?? null,
        timezone: doc.timezone ?? null,
        media: doc.media ?? null,
      },
      { shouldDirty: true },
    );
  };

  return (
    <EntityRefPicker<any>
      current={
        venue?.id
          ? {
              name: venue.name ?? venue.id,
              subtitle: [venue.address, venue.timezone]
                .filter(Boolean)
                .join(" · "),
              imageUrl: venue.media?.photos?.[0]?.url,
              href: `/venues/edit/${venue.id}`,
            }
          : null
      }
      emptyLabel="No venue"
      onSearch={search}
      accessorFn={(doc) => ({
        id: doc?.id ?? "",
        name: [doc?.name, doc?.address].filter(Boolean).join(" · "),
        iconUrl: doc?.media?.photos?.[0]?.url,
      })}
      onPick={pick}
    />
  );
}
